import { Button, Loader, Tabs } from '@mantine/core'
import { IconArrowRight, IconPlus, IconUsers } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useDuplicateReviewCounts,
  useDuplicateReviewList,
  type DuplicateReviewStatusTab,
  type DuplicateReviewSummary,
} from '@/hooks/usePatientMerge'
import { MatchStrengthPill } from './components/MatchStrengthPill'
import { ReviewSignals } from './components/ReviewSignals'

const TABS: { value: DuplicateReviewStatusTab; label: string }[] = [
  { value: 'awaiting', label: 'Awaiting review' },
  { value: 'later', label: 'Review later' },
  { value: 'resolved', label: 'Resolved' },
]

/**
 * Duplicate review queue (PD-1986): the pairs the middleware detected or a practitioner opened.
 * Open to every practitioner; the server decides which reviews the caller can see.
 */
export function DuplicateQueuePage() {
  const [tab, setTab] = useState<DuplicateReviewStatusTab>('awaiting')
  const counts = useDuplicateReviewCounts()
  const list = useDuplicateReviewList(tab)
  const reviews = useMemo(() => (list.data?.pages ?? []).flatMap((p) => p.data), [list.data])
  const tabCount: Partial<Record<DuplicateReviewStatusTab, number>> = {
    awaiting: counts.data?.awaiting,
    later: counts.data?.later,
  }

  return (
    <div className="box-border min-h-[calc(100vh-64px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="flex flex-col gap-4 rounded-[18px] border border-[#E7E9EC] bg-white px-6 pt-6 pb-4 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]">
        <div className="flex w-full flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <IconUsers size={22} className="text-[#E5533D]" stroke={2} aria-hidden />
              <h1 className="text-xl font-bold tracking-tight text-[#1F2733]">Duplicate review</h1>
            </div>
            <p className="max-w-[70ch] text-sm text-[#6B7280]">
              Patient records that look like the same person. Open a pair to compare the records and
              choose which one to keep.
            </p>
          </div>
          <Button
            component={Link}
            to="/patients"
            variant="default"
            leftSection={<IconPlus size={16} aria-hidden />}
            className="shrink-0"
          >
            Select two patient records
          </Button>
        </div>

        <Tabs value={tab} onChange={(value) => value && setTab(value as DuplicateReviewStatusTab)}>
          <Tabs.List>
            {TABS.map(({ value, label }) => (
              <Tabs.Tab key={value} value={value}>
                {label}
                {tabCount[value] !== undefined && (
                  <span className="ml-2 rounded-full bg-[#F1F2F4] px-2 py-0.5 text-xs font-semibold text-[#6B7280]">
                    {tabCount[value]}
                  </span>
                )}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        {list.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : list.isError ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <p className="text-sm font-medium text-[#6B7280]">
              Couldn&apos;t load duplicate reviews.
            </p>
            <Button variant="default" loading={list.isFetching} onClick={() => void list.refetch()}>
              Retry
            </Button>
          </div>
        ) : reviews.length === 0 ? (
          <p className="py-10 text-center text-sm font-medium text-[#6B7280]">
            No records to review
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {reviews.map((review) => (
              <li key={review.reviewId}>
                <ReviewPairCard review={review} />
              </li>
            ))}
          </ul>
        )}

        {list.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="default"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

/** One pair: why it was flagged and the way in, above the two records side by side. */
function ReviewPairCard({ review }: { review: DuplicateReviewSummary }) {
  return (
    <article
      aria-label={review.records.map((record) => record.name).join(' and ')}
      className="flex flex-col gap-3 rounded-xl border border-[#E7E9EC] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <MatchStrengthPill strength={review.strength} />
          <p className="text-sm text-[#1F2733]">
            <span className="text-[#6B7280]">Why they match: </span>
            <span className="font-semibold">{review.reasonText}</span>
          </p>
        </div>
        <Button
          component={Link}
          to={`/patients/duplicates/${review.reviewId}`}
          variant="outline"
          color="#E5533D"
          rightSection={<IconArrowRight size={16} aria-hidden />}
          className="shrink-0"
        >
          Review pair
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {review.records.map((record, index) => (
          <div
            key={record.patientId}
            className="flex min-w-0 flex-col gap-2.5 rounded-lg border border-[#EEF0F2] bg-[#FAFBFC] px-4 py-3"
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-[#D8DBE0] bg-white text-xs font-bold text-[#4A5160]"
              >
                {index + 1}
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="font-semibold break-words text-[#1F2733]">{record.name}</span>
                <span className="text-sm break-all text-[#6B7280]">
                  {record.email ?? 'No email'}
                </span>
              </div>
            </div>
            <div className="pl-9">
              <ReviewSignals signals={review.signals[index]} />
            </div>
          </div>
        ))}
      </div>
    </article>
  )
}
